import React from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import { visuallyHidden } from '@mui/utils';
import type { FC, PropsWithChildren, ReactNode } from 'react';

import PageTitle from '../../../ui/PageTitle/PageTitle';
import Title from '../../../ui/Title/Title';
import { TITLE_IDS } from '../../../utils/element-ids';
import IconRenderer from '../../../ui/Icons/IconRenderer';

const FaqTitle: FC<PropsWithChildren> = ({ children }) => {
  return (
    <Typography
      variant="body1"
      style={{ fontWeight: 'bold' }}
      component="h3"
    >
      <span
        dangerouslySetInnerHTML={{
          __html: children?.toString() ?? '',
        }}
      />
    </Typography>
  );
};

const FaqContent: FC<PropsWithChildren> = ({ children }) => {
  return (
    <Typography variant="body1">
      {typeof children === 'string' ? (
        <span
          dangerouslySetInnerHTML={{
            __html: children?.toString() ?? '',
          }}
        />
      ) : (
        children
      )}
    </Typography>
  );
};

const FaqSection: FC<{
  title: string;
  content: string | ReactNode;
}> = ({ title, content }) => {
  return (
    <Box sx={{ mt: '24px' }}>
      <FaqTitle>{title}</FaqTitle>

      <Box>
        <FaqContent>{content}</FaqContent>
      </Box>
    </Box>
  );
};

const HelpContent = () => {
  return (
    <>
      <PageTitle
        title="Need help?"
        subTitle="Contact the Infusion billing department if you have questions."
        titleId={TITLE_IDS.HELP_MODAL_TITLE_ID}
      />

      <Link href="tel:+18008641338">
        <Typography variant="h5">1-800-864-1338</Typography>
      </Link>

      <Box sx={{ mt: '40px' }}>
        <Title title="FAQ" />
      </Box>

      <FaqSection
        title="What is Login to Pay?"
        content="Login to pay is a payment solution that enables consumers to store and manage their payment methods across participating UnitedHealth Group businesses, for use now and in the future. Login to pay leverages Optum and United’s enterprise technology – our leading security technology used to sign-in to any portal across UnitedHealth Group – and the advanced encryption technology of our industry-leading payment partner."
      />

      <FaqSection
        title="Is Login to Pay secure?"
        content="Login to pay is highly secure. All card information is encrypted and stored in-line with industry standards and payment card industry regulations. Businesses using Login to pay will never have access to your credit card information."
      />

      <FaqSection
        title="What payment methods are accepted?"
        content="Credit card brands accepted are:
        <ul style=margin-block-start:0>
        <li>Visa</li>
        <li>Mastercard</li>
        <li>American Express</li>
        <li>China UnionPay (CUP)</li>
        <li>Discover &amp; Diners</li>
        <li>Japan Credit Bureau (JCB)</li>
        </ul>
        A wide range of payment options are offered, depending on which UnitedHealthcare or Optum site you are using. Other payment methods may include Apple Pay and Google Pay, Buy Now Pay Later, health savings account (HSA) cards or ACH (paying with your bank account)."
      />

      <FaqSection
        title="What type of payments can I make with Login to Pay?"
        content="You can pay for any products, services or care, across participating Optum and UnitedHealthcare businesses."
      />

      <FaqSection
        title="Am I required to store payment methods?"
        content="No, storing payment methods is optional; however, it is a convenient way to access payment credentials at time of checkout and have a consistent checkout experience as you navigate the cost of care."
      />

      <FaqSection
        title="Who do I contact for assistance with <em>Login to Pay</em>?"
        content={
          <>
            For questions and assistance, please contact the
            UnitedHealthcare or Optum business you are making a
            payment through.
            <br />
            <br />
            Contact information can be found at{' '}
            <Link
              href="https://uhc.com/contact-us"
              target="_blank"
            >
              <Grid
                container
                component="span"
              >
                uhc.com/contact-us{' '}
                <span style={visuallyHidden}>
                  opens in a new window
                </span>
                <IconRenderer
                  name="launch"
                  testId="helpContentLaunchIcon"
                />
              </Grid>
            </Link>{' '}
            or{' '}
            <Link
              href="https://optum.com/contact-us"
              target="_blank"
            >
              <Grid
                container
                component="span"
              >
                optum.com/contact-us{' '}
                <span style={visuallyHidden}>
                  opens in a new window
                </span>
                <IconRenderer
                  name="launch"
                  testId="helpContentLaunchIcon2"
                />
              </Grid>
            </Link>{' '}
          </>
        }
      />
    </>
  );
};

export default HelpContent;
